import React from 'react'
import { FormattedMessage } from 'react-intl'

import { IProposalInteractionsProps } from '../proposal/Proposal'
import ProposalDetailsPlanning from '../proposal/proposal-details/proposal-details-planning/ProposalDetailsPlanning'
import ProposalDetailsSummary from '../proposal/proposal-details/proposal-details-summary/ProposalDetailsSummary'
import { ProposalModalHeader } from '../proposal-modal-header/ProposalModalHeader'
import {
  SuggestionConfirmBookingFooter,
  SuggestionConfirmBookingFooterActionMode,
} from '../../molecules/suggestion-confirm-booking-footer/SuggestionConfirmBookingFooter'
import {
  ClientCarbonFootprintInputDto,
  ComputedPrice,
  Maybe,
  NonCompliantRulesFragment,
  ProposalDetailsPlanningPlanningItemFragment,
  ProposalDetailsSummaryCarbonEmissionsFragment,
  ProposalDetailsSummaryClientPricingItemFragment,
  ProposalDetailsSummaryPriceFragment,
  TaxIdentifierEnum,
  TravelTypeEnum,
} from '../../../lib/graphql'
import { Dialog, DialogContent } from '../../../shadcn/components/ui/dialog'
import { Separator } from '../../../shadcn/components/ui/separator'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../shadcn/components/ui/tabs'
import { cn } from '../../../shadcn/utils'
import { getHouseLocation } from '../../../utils'

export type ClientProposalDetailsModalActionMode =
  | {
      mode: 'can-confirm'
      action?: NonNullable<
        IProposalInteractionsProps['userActions']
      >['userConfirmedBooking']
    }
  | {
      mode: 'can-confirm-non-compliant'
      nonCompliantRules: NonCompliantRulesFragment
      action?: NonNullable<
        IProposalInteractionsProps['userActions']
      >['userAskedUserAdminApproval']
    }
  | {
      mode: 'estimation'
    }

type ClientProposalDetailsModalProps = {
  isOpen: boolean
  closeModal: () => void
  clientProposalId: string
  clientRequestId: string
  clientProposal: {
    houseId?: Maybe<string>
    startDate?: Maybe<Date>
    endDate?: Maybe<Date>
    adults?: Maybe<number>
    house?: Maybe<{
      title: string
      taxIdentifier: TaxIdentifierEnum
      coverPicture: {
        uri: string
      }
      address?: Maybe<{
        longitude?: Maybe<number>
        latitude?: Maybe<number>
      }>
    }>
    price?: Maybe<
      ProposalDetailsSummaryPriceFragment & {
        totalUser?: Maybe<{
          priceWithDiscountAndFees?: Maybe<{
            priceWithVat: number
            priceWithoutVat: number
          }>
        }>
      }
    >
    clientPricingItems?: Maybe<
      ProposalDetailsSummaryClientPricingItemFragment[]
    >
    deposit?: Maybe<{
      price?: Maybe<{
        totalUser?: Maybe<{
          priceWithDiscountAndFees?: Maybe<ComputedPrice>
        }>
      }>
    }>
    type: TravelTypeEnum
    permissions: {
      canUpdateProposal: boolean
    }
    planning?: Maybe<ProposalDetailsPlanningPlanningItemFragment[]>
  }
  carbonFootprint?: {
    isLoading: boolean
    onValidate: (carbonFootprint: ClientCarbonFootprintInputDto) => void
    carbonFootprintInfos?: ProposalDetailsSummaryCarbonEmissionsFragment | null
  }
} & ClientProposalDetailsModalActionMode

export const ClientProposalDetailsModal = ({
  isOpen,
  closeModal,
  clientProposalId,
  clientRequestId,
  clientProposal,
  ...props
}: ClientProposalDetailsModalProps) => {
  const tabClassNames = cn(
    'h-full flex-1 rounded-none py-4 font-normal',
    'data-[state=active]:border-x-0 data-[state=active]:border-b data-[state=active]:border-t-0 data-[state=active]:border-solid data-[state=active]:bg-transparent data-[state=active]:shadow-none',
  )

  const getFooterActionProps = (): SuggestionConfirmBookingFooterActionMode => {
    if (props.mode === 'estimation') {
      return {
        confirmAction: 'none',
      }
    }

    const { action } = props

    const commonProps = { isLoading: action?.isLoading ?? false }

    switch (props.mode) {
      case 'can-confirm':
        return {
          confirmAction: 'can-confirm',
          callback: ({ clientProposalId }) =>
            action?.callback({ clientProposalId }),
          ...commonProps,
        }
      case 'can-confirm-non-compliant':
        return {
          confirmAction: 'can-ask-admin-approval',
          callback: ({ clientProposalId, comment }) =>
            action?.callback({ clientProposalId, comment }),
          ...commonProps,
        }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent
        backdropStyle='dark'
        className={cn(
          'gap-0 p-0',
          'md:w-[90vw] md:max-w-[1400px]',
          'lg:w-[90vw] lg:max-w-[1400px]',
        )}
      >
        <div
          className={cn(
            'flex h-screen flex-col',
            'md:h-[90vh] md:max-h-[1000px]',
          )}
        >
          <ProposalModalHeader
            startDate={clientProposal.startDate}
            endDate={clientProposal.endDate}
            title={clientProposal.house?.title}
            adults={clientProposal.adults}
            picture={clientProposal.house?.coverPicture?.uri}
            onClose={closeModal}
            nonCompliantRules={
              props.mode === 'can-confirm-non-compliant'
                ? props.nonCompliantRules
                : undefined
            }
          />
          <Separator />
          <div className='mb-3 min-h-0 flex-1'>
            <Tabs defaultValue='summary' className='flex max-h-full flex-col'>
              <TabsList className='h-12 bg-transparent p-0'>
                <TabsTrigger value='summary' className={tabClassNames}>
                  <FormattedMessage
                    id='app.SuggestionProposalDetailsModal.summaryTab'
                    defaultMessage='Synthèse des prestations'
                  />
                </TabsTrigger>
                <TabsTrigger value='planning' className={tabClassNames}>
                  <FormattedMessage
                    id='app.SuggestionProposalDetailsModal.planningTab'
                    defaultMessage='Planning'
                  />
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value='summary'
                className='mt-[1px] overflow-y-auto px-4'
              >
                <ProposalDetailsSummary
                  price={clientProposal.price ?? {}}
                  adults={clientProposal.adults ?? 0}
                  clientPricingItems={clientProposal.clientPricingItems ?? []}
                  isModal={true}
                  taxIdentifier={clientProposal.house?.taxIdentifier}
                  depositAmount={
                    clientProposal.deposit?.price?.totalUser
                      ?.priceWithDiscountAndFees ?? undefined
                  }
                  onValidateCarbonFootprintInfos={
                    props.carbonFootprint?.onValidate
                  }
                  isCarbonFootprintLoading={props.carbonFootprint?.isLoading}
                  carbonFootprintInfos={
                    props.carbonFootprint?.carbonFootprintInfos
                  }
                  houseLocation={
                    getHouseLocation({
                      address: clientProposal.house?.address,
                    }) ?? undefined
                  }
                />
              </TabsContent>
              <TabsContent
                value='planning'
                className='mt-[1px] overflow-y-auto px-4'
              >
                <div className='my-5'>
                  <ProposalDetailsPlanning
                    clientPlanningItemsByDates={clientProposal.planning ?? []}
                    isModal={true}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <Separator />
          <SuggestionConfirmBookingFooter
            clientProposalId={clientProposalId}
            clientRequestId={clientRequestId}
            priceWithVat={
              clientProposal.price?.totalUser?.priceWithDiscountAndFees
                ?.priceWithVat
            }
            priceWithoutVat={
              clientProposal.price?.totalUser?.priceWithDiscountAndFees
                ?.priceWithoutVat
            }
            houseId={clientProposal.houseId ?? undefined}
            {...getFooterActionProps()}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
